import { memo, useEffect, useState } from 'react';
import { useTypewriter } from '../hooks/useTypewriter';
import { cn } from '../lib/cn';
import { LogoMark } from './LogoMark';

/**
 * Header indicator shown when the assistant turn is streaming but
 * we haven't seen any content yet: the mark's dot pulsing and a light
 * band sweeping the label. Once any text/tool/thinking arrives, the
 * parent removes this and the real content takes over.
 */
export const ThinkingIndicator = memo(function ThinkingIndicator({
  startedAt,
  // Lets the parent override the verb (we use "Thinking" by default
  // but the empty-message branch in MessageRow can pass "Reading
  // your message" or similar; the thinking-block uses "Reasoning").
  label,
}: {
  startedAt: number | null;
  label?: string;
}) {
  const elapsed = useElapsedSeconds(startedAt);
  return (
    <div className="mc-enter my-2 inline-flex items-center gap-2 py-1 text-[14px] text-muted-foreground">
      <LogoMark className="h-3.5 w-auto" motion="think" />
      <span className="mc-shimmer">{label ?? 'Думаю'}</span>
      {elapsed !== null && elapsed >= 1 && (
        <span className="tabular-nums text-muted-foreground/80">· {elapsed} с</span>
      )}
    </div>
  );
});

/**
 * Same elapsed timer the composer status uses, scoped locally so
 * the indicator doesn't need a heavier shared store. Re-renders at
 * 1 Hz — cheap.
 */
function useElapsedSeconds(startedAt: number | null): number | null {
  const [elapsed, setElapsed] = useState<number | null>(
    startedAt === null ? null : Math.max(0, Math.floor((Date.now() - startedAt) / 1000)),
  );
  useEffect(() => {
    if (startedAt === null) {
      setElapsed(null);
      return;
    }
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startedAt]);
  return elapsed;
}

/**
 * Extended-thinking disclosure. Native `<details>` matching
 * mocks/index.html — chevron + muted summary, no frame, no shimmer.
 */
export function ThinkingBlock({
  text,
  streaming,
  startedAt,
}: {
  text: string;
  streaming: boolean;
  startedAt: number | null;
}) {
  const typed = useTypewriter(text, { isStreaming: streaming });
  const elapsed = useElapsedSeconds(streaming ? startedAt : null);
  const [finalElapsed, setFinalElapsed] = useState<number | null>(null);
  useEffect(() => {
    if (!streaming && startedAt !== null && finalElapsed === null) {
      setFinalElapsed(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    }
    if (streaming) setFinalElapsed(null);
  }, [streaming, startedAt, finalElapsed]);

  const summary = streaming
    ? elapsed !== null && elapsed >= 1
      ? `Размышляю · ${elapsed} с`
      : 'Размышляю'
    : finalElapsed !== null && finalElapsed >= 1
      ? `Размышлял ${finalElapsed} с`
      : 'Ход мыслей';

  return (
    <details className="group/think mb-3" open>
      <summary className="inline-flex cursor-pointer list-none items-center gap-1.5 py-1 text-[14px] text-muted-foreground hover:text-foreground [&::-webkit-details-marker]:hidden">
        <Chevron className="shrink-0 transition-transform group-open/think:rotate-90" />
        <span className={streaming ? 'mc-shimmer' : undefined}>{summary}</span>
      </summary>
      <div className="mt-2 ml-4 whitespace-pre-wrap border-l-2 border-border pl-3 text-[13px] text-muted-foreground">
        {typed}
      </div>
    </details>
  );
}

function Chevron({ className }: { className?: string }) {
  return (
    <svg
      className={cn(className)}
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="4 3 8 6 4 9" />
    </svg>
  );
}
